import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { EventTicket, UserTicketPurchase, MemberUser, SiteSettings, LanguageCode } from '../types';
import {
  Ticket,
  Calendar,
  MapPin,
  CreditCard,
  Building,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  QrCode,
  Sparkles,
  Search,
  User,
  Phone,
  Mail,
  Check,
  Download,
  ShieldCheck,
  ChevronRight,
  Info,
  X,
  Lock,
} from 'lucide-react';
import { createTicketPurchaseApi } from '../services/api';

interface TicketStoreViewProps {
  currentLang: LanguageCode;
  eventTickets: EventTicket[];
  userPurchases: UserTicketPurchase[];
  setUserPurchases: React.Dispatch<React.SetStateAction<UserTicketPurchase[]>>;
  currentUser: MemberUser | null;
  onOpenAuth: () => void;
  settings: SiteSettings;
  onNavClick: (tab: string) => void;
}

export const TicketStoreView: React.FC<TicketStoreViewProps> = ({
  eventTickets,
  userPurchases,
  setUserPurchases,
  currentUser,
  onOpenAuth,
  settings,
  onNavClick,
}) => {
  const [activeViewTab, setActiveViewTab] = useState<'available' | 'my_tickets'>('available');
  const [selectedTicket, setSelectedTicket] = useState<EventTicket | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Purchase Form State
  const [buyerName, setBuyerName] = useState(currentUser?.fullName || '');
  const [buyerPhone, setBuyerPhone] = useState(currentUser?.phone || '');
  const [buyerEmail, setBuyerEmail] = useState(currentUser?.email || '');
  const [paymentMethod, setPaymentMethod] = useState<'credit_card' | 'bank_transfer'>('credit_card');
  const [cardNumber, setCardNumber] = useState('4543 8812 3004 7712');
  const [cardExpiry, setCardExpiry] = useState('12/28');
  const [cardCvc, setCardCvc] = useState('841');

  // QR Modal State for User Ticket
  const [selectedUserTicketQr, setSelectedUserTicketQr] = useState<{ ticket: UserTicketPurchase; qrImage: string } | null>(null);

  // Status Filter for My Tickets
  const [myTicketsFilter, setMyTicketsFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'suspended'>('all');

  useEffect(() => {
    if (currentUser) {
      if (!buyerName) setBuyerName(currentUser.fullName);
      if (!buyerPhone) setBuyerPhone(currentUser.phone || '');
      if (!buyerEmail) setBuyerEmail(currentUser.email);
    }
  }, [currentUser]);

  const activeEventTickets = eventTickets.filter((t) => t.isActive);

  const filteredEventTickets = activeEventTickets.filter(
    (t) =>
      t.eventName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.ticketTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.address.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const myPurchasedTickets = userPurchases.filter((p) => {
    if (!currentUser) return false;
    return p.userId === currentUser.id || p.userEmail === currentUser.email;
  });

  const filteredMyTickets = myPurchasedTickets.filter((t) => {
    if (myTicketsFilter === 'all') return true;
    return t.status === myTicketsFilter;
  });

  const handleOpenPurchaseModal = (ticket: EventTicket) => {
    if (!currentUser) {
      if (confirm('Bilet satın almak için önce üye girişi yapmanız gerekmektedir. Giriş yapmak ister misiniz?')) {
        onOpenAuth();
      }
      return;
    }
    setSelectedTicket(ticket);
  };

  const handleConfirmPurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket) return;

    if (!buyerName || !buyerPhone || !buyerEmail) {
      alert('Lütfen ad soyad, telefon ve e-posta bilgilerinizi eksiksiz giriniz.');
      return;
    }

    const uniqueTicketId = `TK-${Math.floor(100000 + Math.random() * 900000)}`;

    const newPurchase: UserTicketPurchase = {
      id: uniqueTicketId,
      ticketId: selectedTicket.id,
      eventName: selectedTicket.eventName,
      ticketTitle: selectedTicket.ticketTitle,
      address: selectedTicket.address,
      date: selectedTicket.date,
      time: selectedTicket.time,
      price: selectedTicket.price,
      currency: selectedTicket.currency || '€',
      userId: currentUser?.id || 'guest',
      userFullName: buyerName,
      userPhone: buyerPhone,
      userEmail: buyerEmail,
      paymentMethod,
      paymentReference: paymentMethod === 'credit_card' ? `POS-CC-${Date.now().toString().slice(-6)}` : `HAVALE-REF-${Date.now().toString().slice(-6)}`,
      status: 'pending', // Pending Admin approval
      isUsed: false,
      qrCode: `${uniqueTicketId}:${selectedTicket.id}`,
      createdAt: new Date().toISOString(),
    };

    setUserPurchases((prev) => [newPurchase, ...prev]);

    try {
      await createTicketPurchaseApi(newPurchase);
    } catch (err) {
      console.error('Purchase ticket error:', err);
    }

    setSelectedTicket(null);
    setActiveViewTab('my_tickets');
    alert(
      `🎉 TEBRİKLER! Bilet siparişiniz başarıyla alındı.\nBilet No: ${uniqueTicketId}\nÖdeme Yöntemi: ${paymentMethod === 'credit_card' ? 'Kredi Kartı' : 'Bank Transfer / Havale'}\n\nBiletiniz yönetim onayına sunulmuştur. Onaylandıktan sonra "Onaylı Biletlerim" menüsünden QR kodunuzu görüntüleyebilirsiniz.`
    );
  };

  const handleShowQrModal = async (ticket: UserTicketPurchase) => {
    try {
      // Generate QR Code data URL
      const qrData = `${ticket.id}:${ticket.ticketId || ticket.eventName}`;
      const url = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#0B1426',
          light: '#FFFFFF',
        },
      });
      setSelectedUserTicketQr({ ticket, qrImage: url });
    } catch (err) {
      console.error('QR generation error:', err);
      alert('QR Kod üretilemedi.');
    }
  };

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8 animate-fadeIn">
      {/* 1. HERO HEADER */}
      <div className="relative rounded-3xl overflow-hidden glass-card border border-[#38BDF8]/30 bg-gradient-to-r from-[#0B1426] via-[#0F283E] to-[#0A324A] p-8 md:p-12 shadow-2xl text-white">
        <div className="absolute -right-10 -bottom-10 w-80 h-80 bg-[#38BDF8]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#38BDF8]/20 border border-[#38BDF8]/40 text-[#7DD3FC] text-xs font-bold uppercase tracking-wider">
            <Ticket className="w-4 h-4 text-[#38BDF8]" />
            <span>Resmi QR Kodlu Online Bilet Portalı</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading tracking-tight text-white leading-tight">
            Etkinlik & Konser Bilet Satış Menüsü
          </h1>
          <p className="text-gray-300 text-sm md:text-base leading-relaxed font-sans">
            Derneğimizin düzenlediği konser, panel ve festivallere resmi QR kodlu dijital biletinizle saniyeler içinde katılın. Satın aldığınız biletler yönetici onayından geçerek doğrudan profilinize ve QR kodunuza yansır.
          </p>
        </div>
      </div>

      {/* 2. NAVIGATION TABS */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveViewTab('available')}
            className={`px-5 py-3 rounded-2xl font-bold text-xs sm:text-sm flex items-center gap-2 transition-all ${
              activeViewTab === 'available'
                ? 'bg-gradient-to-r from-[#38BDF8] to-[#0284C7] text-[#0B1426] shadow-[0_0_20px_rgba(56,189,248,0.5)] font-black'
                : 'bg-[#132238] text-gray-300 hover:text-white border border-white/10'
            }`}
          >
            <Ticket className="w-4 h-4" />
            <span>Satıştaki Biletler ({activeEventTickets.length})</span>
          </button>

          <button
            onClick={() => setActiveViewTab('my_tickets')}
            className={`px-5 py-3 rounded-2xl font-bold text-xs sm:text-sm flex items-center gap-2 transition-all ${
              activeViewTab === 'my_tickets'
                ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-[#0B1426] shadow-[0_0_20px_rgba(212,175,55,0.5)] font-black'
                : 'bg-[#132238] text-gray-300 hover:text-white border border-white/10'
            }`}
          >
            <QrCode className="w-4 h-4" />
            <span>Sipariş Ettiğim Biletlerim ({myPurchasedTickets.length})</span>
          </button>
        </div>

        {/* Search Input for Available Tickets */}
        {activeViewTab === 'available' && (
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Etkinlik veya bilet ara..."
              className="w-full pl-10 pr-4 py-2 rounded-xl bg-[#0B1426]/90 border border-white/10 text-white placeholder-gray-400 text-xs focus:outline-none focus:border-[#38BDF8]"
            />
          </div>
        )}
      </div>

      {/* 3. AVAILABLE EVENT TICKETS VIEW */}
      {activeViewTab === 'available' && (
        <div className="space-y-6">
          {filteredEventTickets.length === 0 ? (
            <div className="glass-card rounded-3xl p-12 text-center space-y-4 border border-white/10">
              <Ticket className="w-12 h-12 text-gray-500 mx-auto" />
              <h3 className="text-lg font-bold text-white">Şu Anda Satışta Bilet Bulunmamaktadır</h3>
              <p className="text-xs text-gray-400">Yeni etkinlikler yakında duyurulacaktır.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEventTickets.map((t) => (
                <div
                  key={t.id}
                  className="glass-card rounded-3xl border border-[#38BDF8]/30 bg-[#0B1426]/90 overflow-hidden flex flex-col hover:border-[#38BDF8] hover:shadow-[0_0_25px_rgba(56,189,248,0.3)] transition-all duration-300 group"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden bg-[#132238]">
                    <img
                      src={t.image}
                      alt={t.ticketTitle}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1426] via-transparent to-black/30" />
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-sm shadow-md">
                      {t.price} {t.currency || '€'}
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-6 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <span className="text-[10px] font-bold text-[#38BDF8] uppercase tracking-wider block">
                        {t.eventName}
                      </span>
                      <h3 className="text-lg font-black text-white font-heading group-hover:text-[#38BDF8] transition-colors">
                        {t.ticketTitle}
                      </h3>
                      <p className="text-xs text-gray-300 line-clamp-3 leading-relaxed font-sans">
                        {t.description}
                      </p>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-white/10 text-xs text-gray-300">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#38BDF8] flex-shrink-0" />
                        <span>Tarih: <strong>{t.date} {t.time ? `- ${t.time}` : ''}</strong></span>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-[#38BDF8] flex-shrink-0 mt-0.5" />
                        <span className="line-clamp-2">{t.address}</span>
                      </div>

                      <button
                        onClick={() => handleOpenPurchaseModal(t)}
                        className="w-full py-3 rounded-2xl bg-gradient-to-r from-[#38BDF8] to-[#0284C7] hover:from-[#7DD3FC] hover:to-[#38BDF8] text-[#0B1426] font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-lg transform group-hover:scale-[1.02]"
                      >
                        <CreditCard className="w-4 h-4" />
                        <span>Bilet Satın Al ({t.price} {t.currency || '€'})</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 4. MY PURCHASED TICKETS VIEW */}
      {activeViewTab === 'my_tickets' && (
        <div className="space-y-6">
          {!currentUser ? (
            <div className="glass-card rounded-3xl p-10 text-center space-y-4 border border-amber-500/30 bg-amber-950/20">
              <Lock className="w-12 h-12 text-amber-400 mx-auto" />
              <h3 className="text-lg font-bold text-white">Sipariş Biletlerinizi Görmek İçin Üye Girişi Yapınız</h3>
              <p className="text-xs text-gray-300 max-w-md mx-auto">
                Biletlerinizi görüntülemek ve kapıda göstereceğiniz dinamik QR kodunuza erişmek için hesabınıza giriş yapın.
              </p>
              <button
                onClick={onOpenAuth}
                className="px-6 py-2.5 rounded-xl bg-[#D4AF37] hover:bg-[#B8860B] text-[#0B1426] font-bold text-xs transition-all"
              >
                Giriş Yap / Kayıt Ol
              </button>
            </div>
          ) : (
            <>
              {/* Status Filter Tabs */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setMyTicketsFilter('all')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    myTicketsFilter === 'all'
                      ? 'bg-white text-[#0B1426]'
                      : 'bg-[#132238] text-gray-300 hover:text-white'
                  }`}
                >
                  Tüm Biletlerim ({myPurchasedTickets.length})
                </button>
                <button
                  onClick={() => setMyTicketsFilter('pending')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    myTicketsFilter === 'pending'
                      ? 'bg-amber-500 text-[#0B1426]'
                      : 'bg-amber-950/40 text-amber-300 border border-amber-500/30'
                  }`}
                >
                  ⏳ Bekleyenler ({myPurchasedTickets.filter((t) => t.status === 'pending').length})
                </button>
                <button
                  onClick={() => setMyTicketsFilter('approved')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    myTicketsFilter === 'approved'
                      ? 'bg-emerald-500 text-[#0B1426]'
                      : 'bg-emerald-950/40 text-emerald-300 border border-emerald-500/30'
                  }`}
                >
                  ✅ Onaylı Biletler ({myPurchasedTickets.filter((t) => t.status === 'approved').length})
                </button>
                <button
                  onClick={() => setMyTicketsFilter('rejected')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    myTicketsFilter === 'rejected'
                      ? 'bg-rose-500 text-white'
                      : 'bg-rose-950/40 text-rose-300 border border-rose-500/30'
                  }`}
                >
                  ❌ Reddedilenler ({myPurchasedTickets.filter((t) => t.status === 'rejected').length})
                </button>
              </div>

              {filteredMyTickets.length === 0 ? (
                <div className="glass-card rounded-3xl p-10 text-center space-y-3 border border-white/10">
                  <Ticket className="w-10 h-10 text-gray-500 mx-auto" />
                  <p className="text-sm font-semibold text-gray-300">Bu kategoride henüz bilete sahip değilsiniz.</p>
                  <button
                    onClick={() => setActiveViewTab('available')}
                    className="text-xs text-[#38BDF8] underline font-bold"
                  >
                    Satıştaki biletleri incelemek için tıklayın
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredMyTickets.map((ticket) => {
                    const isApproved = ticket.status === 'approved';
                    const isPending = ticket.status === 'pending';
                    const isRejected = ticket.status === 'rejected';
                    const isSuspended = ticket.status === 'suspended';

                    return (
                      <div
                        key={ticket.id}
                        className={`glass-card rounded-3xl border p-6 space-y-5 transition-all relative overflow-hidden ${
                          isApproved
                            ? 'border-emerald-500/40 bg-gradient-to-br from-[#0B1426] via-[#0F2820] to-[#0A3D2E]'
                            : isPending
                            ? 'border-amber-500/40 bg-gradient-to-br from-[#0B1426] via-[#2A1F0D] to-[#1A1408]'
                            : isRejected
                            ? 'border-rose-500/40 bg-gradient-to-br from-[#0B1426] via-[#280F15] to-[#1A080C]'
                            : 'border-gray-500/40 bg-[#0B1426]'
                        }`}
                      >
                        {/* Status Ribbon Badge */}
                        <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-3">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-bold text-[#D4AF37] bg-black/40 px-2.5 py-1 rounded-lg border border-white/10">
                              {ticket.id}
                            </span>
                            {ticket.isUsed && (
                              <span className="px-2.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[10px] font-bold">
                                🛑 OKUNDU / KULLANILDI
                              </span>
                            )}
                          </div>

                          {/* Process Status Badge */}
                          <div>
                            {isApproved && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-extrabold">
                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                                <span>ONAYLANDI</span>
                              </span>
                            )}
                            {isPending && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 text-xs font-extrabold animate-pulse">
                                <Clock className="w-3.5 h-3.5 text-amber-400" />
                                <span>YÖNETİM ONAYI BEKLİYOR</span>
                              </span>
                            )}
                            {isRejected && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/40 text-xs font-extrabold">
                                <XCircle className="w-3.5 h-3.5 text-rose-400" />
                                <span>REDDEDİLDİ</span>
                              </span>
                            )}
                            {isSuspended && (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-500/20 text-gray-300 border border-gray-500/40 text-xs font-extrabold">
                                <AlertTriangle className="w-3.5 h-3.5 text-gray-400" />
                                <span>ASKIYA ALINDI</span>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Ticket Content */}
                        <div className="space-y-2">
                          <span className="text-[10px] font-bold text-[#38BDF8] uppercase tracking-wider block">
                            {ticket.eventName}
                          </span>
                          <h4 className="text-xl font-black text-white font-heading">
                            {ticket.ticketTitle}
                          </h4>
                        </div>

                        {/* Event details */}
                        <div className="grid grid-cols-2 gap-3 text-xs text-gray-300 bg-black/30 p-3.5 rounded-2xl border border-white/5">
                          <div>
                            <span className="text-[10px] text-gray-400 block">Tarih & Saat:</span>
                            <span className="font-bold text-white">{ticket.date} {ticket.time}</span>
                          </div>
                          <div>
                            <span className="text-[10px] text-gray-400 block">Bilet Ücreti:</span>
                            <span className="font-bold text-[#D4AF37]">{ticket.price} {ticket.currency || '€'}</span>
                          </div>
                          <div className="col-span-2">
                            <span className="text-[10px] text-gray-400 block">Adres:</span>
                            <span className="font-medium text-gray-200">{ticket.address}</span>
                          </div>
                        </div>

                        {/* Status Message Footer */}
                        {isPending && (
                          <div className="p-3 rounded-xl bg-amber-950/40 border border-amber-500/30 text-[11px] text-amber-200 space-y-1">
                            <div className="font-bold flex items-center gap-1.5">
                              <Info className="w-3.5 h-3.5 text-amber-400" />
                              <span>Süreç Durumu: Yönetim İncelemesinde</span>
                            </div>
                            <p className="text-amber-300/80">
                              Ödeme dekontu veya kart işlemi dernek yönetimi tarafından kontrol ediliyor. Onaylandığında QR kodunuz aktif olacaktır.
                            </p>
                          </div>
                        )}

                        {isRejected && (
                          <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-500/30 text-[11px] text-rose-200 space-y-1">
                            <div className="font-bold flex items-center gap-1.5">
                              <XCircle className="w-3.5 h-3.5 text-rose-400" />
                              <span>Bilet Reddedildi</span>
                            </div>
                            {ticket.rejectionReason && (
                              <p className="text-rose-300/90">Gerekçe: {ticket.rejectionReason}</p>
                            )}
                          </div>
                        )}

                        {/* Action: Show QR Code Button (Only if Approved) */}
                        {isApproved && (
                          <button
                            onClick={() => handleShowQrModal(ticket)}
                            className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 shadow-lg transition-all transform hover:scale-[1.01]"
                          >
                            <QrCode className="w-4 h-4" />
                            <span>Kapı Giriş QR Kodunu Göster</span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 5. PURCHASE MODAL */}
      {selectedTicket && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
          <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto glass-card rounded-3xl border border-[#38BDF8] bg-[#0B1426] p-6 sm:p-8 space-y-6 text-white shadow-2xl">
            <button
              onClick={() => setSelectedTicket(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Modal Header */}
            <div className="space-y-1">
              <span className="text-xs font-bold text-[#38BDF8] uppercase tracking-wider">
                Online Bilet Satın Alma
              </span>
              <h3 className="text-xl font-black font-heading text-white">
                {selectedTicket.ticketTitle}
              </h3>
              <p className="text-xs text-gray-300">{selectedTicket.eventName}</p>
            </div>

            {/* Summary Box */}
            <div className="p-4 rounded-2xl bg-[#132238] border border-white/10 flex items-center justify-between text-xs">
              <div>
                <span className="text-gray-400 block text-[10px]">Tarih / Saat:</span>
                <span className="font-bold text-white">{selectedTicket.date} {selectedTicket.time}</span>
              </div>
              <div className="text-right">
                <span className="text-gray-400 block text-[10px]">Bilet Fiyatı:</span>
                <span className="font-black text-lg text-[#D4AF37]">
                  {selectedTicket.price} {selectedTicket.currency || '€'}
                </span>
              </div>
            </div>

            {/* Purchase Form */}
            <form onSubmit={handleConfirmPurchase} className="space-y-4">
              <div className="space-y-3">
                <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Bilet Sahibi İletişim Bilgileri
                </h4>

                <div>
                  <label className="text-[11px] text-gray-300 font-semibold block mb-1">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    required
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-[#0B1426] border border-white/20 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-gray-300 font-semibold block mb-1">
                      Telefon Numarası *
                    </label>
                    <input
                      type="text"
                      required
                      value={buyerPhone}
                      onChange={(e) => setBuyerPhone(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-[#0B1426] border border-white/20 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-300 font-semibold block mb-1">
                      E-posta Adresi *
                    </label>
                    <input
                      type="email"
                      required
                      value={buyerEmail}
                      onChange={(e) => setBuyerEmail(e.target.value)}
                      className="w-full px-3.5 py-2.5 rounded-xl bg-[#0B1426] border border-white/20 text-white text-xs focus:outline-none focus:border-[#38BDF8]"
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method Selector */}
              <div className="space-y-3 pt-2 border-t border-white/10">
                <h4 className="text-xs font-bold text-gray-300 uppercase tracking-wider">
                  Ödeme Yöntemi Seçiniz
                </h4>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('credit_card')}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between space-y-2 transition-all ${
                      paymentMethod === 'credit_card'
                        ? 'border-[#38BDF8] bg-[#38BDF8]/10 text-white'
                        : 'border-white/10 bg-[#132238] text-gray-400'
                    }`}
                  >
                    <CreditCard className="w-5 h-5 text-[#38BDF8]" />
                    <div>
                      <span className="font-bold text-xs block text-white">Kredi Kartı</span>
                      <span className="text-[10px] text-gray-400">Anında 3D Secure POS</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('bank_transfer')}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between space-y-2 transition-all ${
                      paymentMethod === 'bank_transfer'
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-white'
                        : 'border-white/10 bg-[#132238] text-gray-400'
                    }`}
                  >
                    <Building className="w-5 h-5 text-[#D4AF37]" />
                    <div>
                      <span className="font-bold text-xs block text-white">Bank Havale / EFT</span>
                      <span className="text-[10px] text-gray-400">Dernek SEPA IBAN</span>
                    </div>
                  </button>
                </div>

                {/* Credit Card Input */}
                {paymentMethod === 'credit_card' && (
                  <div className="p-4 rounded-2xl bg-[#132238]/80 border border-white/10 space-y-3 text-xs">
                    <div>
                      <label className="text-[10px] text-gray-300 block mb-1">Kart Numarası</label>
                      <input
                        type="text"
                        value={cardNumber}
                        onChange={(e) => setCardNumber(e.target.value)}
                        className="w-full px-3 py-2 rounded-xl bg-[#0B1426] border border-white/20 text-white font-mono text-xs"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] text-gray-300 block mb-1">Son Kullanma (AA/YY)</label>
                        <input
                          type="text"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl bg-[#0B1426] border border-white/20 text-white font-mono text-xs"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-300 block mb-1">CVC / CVV</label>
                        <input
                          type="text"
                          value={cardCvc}
                          onChange={(e) => setCardCvc(e.target.value)}
                          className="w-full px-3 py-2 rounded-xl bg-[#0B1426] border border-white/20 text-white font-mono text-xs"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Bank Transfer Info */}
                {paymentMethod === 'bank_transfer' && (
                  <div className="p-4 rounded-2xl bg-[#132238]/80 border border-[#D4AF37]/40 space-y-2 text-xs">
                    <p className="font-bold text-[#D4AF37]">Dernek SEPA IBAN Bilgileri:</p>
                    <p className="text-[11px] text-gray-300">
                      Banka: <strong>{settings.paymentGateway.bankAccountName || 'Kürt Kültür Derneği'}</strong>
                    </p>
                    <p className="font-mono text-white text-[11px] select-all bg-black/40 p-2 rounded-lg border border-white/10">
                      {settings.paymentGateway.iban || 'FR76 3000 4000 0123 4567 8901 234'}
                    </p>
                    <p className="text-[10px] text-amber-300">
                      * Havale yaparken açıklama kısmına Adınız Soyadınızı yazınız. İşlem tamamlandıktan sonra biletiniz onaylanacaktır.
                    </p>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#38BDF8] to-[#0284C7] hover:from-[#7DD3FC] hover:to-[#38BDF8] text-[#0B1426] font-black text-xs uppercase tracking-wider shadow-xl transition-all"
              >
                Ödemeyi Tamamla ve Bileti Oluştur
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 6. QR DISPLAY MODAL */}
      {selectedUserTicketQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fadeIn">
          <div className="relative w-full max-w-sm glass-card rounded-3xl border border-emerald-500 bg-[#0B1426] p-6 text-center space-y-5 text-white shadow-2xl">
            <button
              onClick={() => setSelectedUserTicketQr(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1 pt-2">
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-bold uppercase">
                Resmi Kapı Giriş Bilet Kartı
              </span>
              <h3 className="text-lg font-black font-heading text-white">
                {selectedUserTicketQr.ticket.ticketTitle}
              </h3>
              <p className="text-xs text-gray-300">{selectedUserTicketQr.ticket.eventName}</p>
            </div>

            {/* QR Image */}
            <div className="p-4 bg-white rounded-2xl inline-block border-4 border-emerald-500 shadow-2xl">
              <img
                src={selectedUserTicketQr.qrImage}
                alt="Ticket QR Code"
                className="w-56 h-56 mx-auto"
              />
            </div>

            {/* Pass details */}
            <div className="space-y-1 text-xs text-gray-300 bg-black/40 p-3 rounded-2xl border border-white/10">
              <p className="font-mono text-sm font-extrabold text-[#D4AF37]">
                Kod: {selectedUserTicketQr.ticket.id}
              </p>
              <p>Bilet Sahibi: <strong>{selectedUserTicketQr.ticket.userFullName}</strong></p>
              <p className="text-[10px] text-gray-400">{selectedUserTicketQr.ticket.date} - {selectedUserTicketQr.ticket.address}</p>
            </div>

            <p className="text-[10px] text-emerald-300 italic">
              * Kapıdaki görevliye bu ekranı veya QR kodunu gösteriniz. QR kodu tek kullanımlıktır ve tarandıktan sonra sistem tarafından kapatılır.
            </p>

            <button
              onClick={() => setSelectedUserTicketQr(null)}
              className="w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs"
            >
              Kapat
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
